import { Button } from "@/components/ui/button";
import { ACTIVE_FILTERS_PANEL_SHELL_CLASS } from "@/components/data-table/components/DataTableActiveFiltersBar/activeFiltersBarShared";
import type { ActiveFilterDescriptor } from "@/components/data-table/utils/activeFilterDescriptors";
import { cn } from "@/lib/utils";
import { memo } from "react";
import { useTranslation } from "react-i18next";
import { ActiveFiltersSummaryFilterChip } from "./ActiveFiltersSummaryFilterChip";

interface ActiveFiltersSummaryListProps {
  descriptors: ActiveFilterDescriptor[];
  onRemoveColumnFilter: (columnId: string) => void;
  onClearAll?: () => void;
  className?: string;
  /**
   * When set, each row gets an eye toggle if `table.getColumn(id)?.getCanHide()` allows it.
   */
  getColumnVisibilityToggle?: (
    columnId: string,
  ) => { isVisible: boolean; onToggle: () => void } | undefined;
  /**
   * `surface` — empty state: wrapping filter chips (wide layouts).
   * `popover` — narrow vertical list for the popover.
   */
  layout?: "surface" | "popover";
}

const ActiveFiltersSummaryListComp = ({
  descriptors,
  onRemoveColumnFilter,
  onClearAll,
  className,
  getColumnVisibilityToggle,
  layout = "popover",
}: ActiveFiltersSummaryListProps) => {
  const { t } = useTranslation();
  const isSurface = layout === "surface";

  if (isSurface) {
    return (
      <div className={cn("flex flex-col gap-3", className)}>
        <div className="flex max-h-[42vh] flex-wrap content-start justify-center gap-2 overflow-y-auto">
          {descriptors.map((item) => {
            const columnTitle = item.headerTranslationKey
              ? t(item.headerTranslationKey)
              : item.columnId;
            const visibilityToggle = getColumnVisibilityToggle?.(item.columnId);
            return (
              <ActiveFiltersSummaryFilterChip
                key={item.columnId}
                item={item}
                layout="surface"
                columnTitle={columnTitle}
                onRemove={() => onRemoveColumnFilter(item.columnId)}
                visibilityToggle={visibilityToggle}
              />
            );
          })}
        </div>
        {onClearAll ? (
          <div className="flex justify-center border-t border-border/60 pt-3">
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="h-8 text-xs"
              onClick={onClearAll}
            >
              {t("table.clearFilters")}
            </Button>
          </div>
        ) : null}
      </div>
    );
  }

  const popoverListScroll =
    descriptors.length > 5
      ? "max-h-[min(50vh,16rem)] overflow-y-auto overscroll-contain"
      : "max-h-none overflow-visible";

  return (
    <div className={cn("flex flex-col gap-3", className)}>
      <ul
        className={cn(
          "flex flex-col whitespace-normal",
          ACTIVE_FILTERS_PANEL_SHELL_CLASS,
          popoverListScroll,
        )}
      >
        {descriptors.map((item) => {
          const columnTitle = item.headerTranslationKey
            ? t(item.headerTranslationKey)
            : item.columnId;
          const visibilityToggle = getColumnVisibilityToggle?.(item.columnId);
          return (
            <ActiveFiltersSummaryFilterChip
              key={item.columnId}
              item={item}
              layout="popover"
              columnTitle={columnTitle}
              onRemove={() => onRemoveColumnFilter(item.columnId)}
              visibilityToggle={visibilityToggle}
            />
          );
        })}
      </ul>
      {onClearAll ? (
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="h-8 w-full text-xs"
          onClick={onClearAll}
        >
          {t("table.clearFilters")}
        </Button>
      ) : null}
    </div>
  );
};

export const ActiveFiltersSummaryList = memo(ActiveFiltersSummaryListComp);
